import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Project } from '../../types';
import { dossierAPI, analysisAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { htmlToPlain } from '../common/RichText';
import { BRAND, faseKleur } from '../../styles/brand';
import { FaseTag } from '../flow/FaseTag';
import { StakeholdersOverview, StakeholderForm, Stakeholder, migrateStakeholders, emptyStakeholder, seedFromArea, seedFromFase1, mergeProposals, saveStakeholdersInDossier } from './StakeholdersEditor';

interface StakeholdersModuleProps {
  project: Project;
}

const backBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 7, marginBottom: 16, padding: '6px 10px 6px 6px', borderRadius: 8, border: 'none', background: 'transparent', color: '#6b7280', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' };
const BackIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
);

/**
 * Fase 2 – Stakeholders & stakeholderanalyse: eigen pagina naast Gebiedskaart en Nieuws & sentiment.
 * De lijst blijft in de dossier-sectie 'omgevingsanalyse' (stakeholdersList + tekstsamenvatting), zodat
 * valideren van Fase 2 en de input voor Fase 3 ongewijzigd werken.
 */
export const StakeholdersModule: React.FC<StakeholdersModuleProps> = ({ project }) => {
  const toast = useToast();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [stakeholders, setStakeholders] = useState<Stakeholder[]>([]);
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [area, setArea] = useState<any | null>(null);
  const [fase1Stakeholders, setFase1Stakeholders] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const stakeholderParam = searchParams.get('stakeholder');
  const isCreate = stakeholderParam === 'nieuw';
  const [draft, setDraft] = useState<Stakeholder | null>(null);

  /** false = de fase is (inmiddels) gevalideerd. */
  const saveList = useCallback(async (list: Stakeholder[]): Promise<boolean> => {
    const ok = await saveStakeholdersInDossier(project.id, list);
    if (!ok) setLocked(true);
    return ok;
  }, [project.id]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dossierAPI.getByProject(project.id);
      const fase1 = (res?.dossier?.sections?.projectverkenning?.content) || {};
      const fase1St = htmlToPlain(fase1.belangrijksteStakeholders).replace(/^[-•*]\s+/gm, '').trim();
      setFase1Stakeholders(fase1St);
      const section = res?.dossier?.sections?.omgevingsanalyse;
      const content: any = (section && section.content) || {};
      const isLocked = section?.status === 'locked';
      setLocked(isLocked);
      let sh = migrateStakeholders(content.stakeholdersList, content.stakeholders);
      // Nog nooit stakeholders vastgelegd? Neem de Belangrijkste stakeholders uit Fase 1 als startpunt (eenmalig).
      if (sh.length === 0 && content.stakeholdersList === undefined && fase1St && !isLocked) {
        sh = seedFromFase1(fase1St);
        try { await saveList(sh); } catch (e) { /* niet kritisch */ }
      }
      setStakeholders(sh);
    } catch (e) {
      // dossier wordt server-side aangemaakt zodra beschikbaar
    } finally {
      setLoading(false);
    }
  }, [project.id, saveList]);

  useEffect(() => { load(); }, [load]);

  // Gebiedskaart-data (voor 'Voorstel uit gebiedsanalyse').
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await analysisAPI.getArea(project.id);
        if (!cancelled) setArea(res?.data || null);
      } catch {
        if (!cancelled) setArea(null); // 404 = nog geen gebiedskaart getekend
      }
    })();
    return () => { cancelled = true; };
  }, [project.id]);

  // Aanmaak-concept in sync met de URL (?stakeholder=nieuw) voor browser-back.
  useEffect(() => {
    if (isCreate) setDraft((d) => d || emptyStakeholder());
    else setDraft(null);
  }, [isCreate]);

  const persist = async (list: Stakeholder[]) => {
    setStakeholders(list);
    if (locked) return;
    try {
      const ok = await saveList(list);
      if (!ok) toast.error('Niet opgeslagen', 'De omgevingsanalyse is inmiddels gevalideerd. Heropen de fase om stakeholders te wijzigen.');
    } catch (e: any) {
      toast.error('Opslaan mislukt', e?.response?.data?.error || 'Probeer het opnieuw.');
    }
  };

  const open = (id: string) => setSearchParams({ stakeholder: id });
  const close = () => setSearchParams({});
  const saveAndClose = () => { persist(stakeholders); close(); };
  const create = () => { setDraft(emptyStakeholder()); setSearchParams({ stakeholder: 'nieuw' }); };
  const update = (id: string, k: keyof Stakeholder, v: string) => setStakeholders((l) => l.map((s) => (s.id === id ? { ...s, [k]: v } : s)));
  const remove = (id: string) => { persist(stakeholders.filter((s) => s.id !== id)); close(); };
  const changeDraft = (k: keyof Stakeholder, v: string) => setDraft((d) => (d ? { ...d, [k]: v } : d));
  const saveDraft = () => {
    if (!draft) return;
    if (!draft.naam.trim()) { toast.error('Naam ontbreekt', 'Geef de stakeholder een naam.'); return; }
    const added = draft;
    persist([...stakeholders, added]);
    setDraft(null);
    setSearchParams({ stakeholder: added.id });
    toast.success('Stakeholder toegevoegd', 'Verrijk de stakeholder gerust verder.');
  };
  const seedArea = () => {
    persist(mergeProposals(stakeholders, seedFromArea(area)));
    toast.success('Voorstel toegevoegd', 'Stakeholders uit de gebiedsanalyse overgenomen en opgeslagen. Beoordeel en pas aan.');
  };
  const seedFase1 = () => {
    if (!fase1Stakeholders.trim()) return;
    persist(mergeProposals(stakeholders, seedFromFase1(fase1Stakeholders)));
    toast.success('Overgenomen', 'Stakeholders uit de projectverkenning overgenomen en opgeslagen. Beoordeel en pas aan.');
  };

  if (loading) return <div style={{ padding: 32, color: '#6b7280' }}>Projectdossier laden…</div>;

  // ---- Volledige pagina: stakeholder bewerken/aanmaken ----
  const editing = stakeholderParam && !isCreate ? (stakeholders.find((s) => s.id === stakeholderParam) || null) : null;
  if (editing || (isCreate && draft)) {
    const isDraft = isCreate;
    const s = (editing || draft) as Stakeholder;
    const back = isDraft ? close : saveAndClose;
    return (
      <div style={{ padding: isMobile ? 16 : 32, maxWidth: 1040, margin: '0 auto' }}>
        <button onClick={back} style={backBtn}>
          <BackIcon />
          Terug naar stakeholders
        </button>
        <div style={{ marginBottom: 18 }}>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1a1a2e' }}>{isDraft ? 'Nieuwe stakeholder' : (s.naam || 'Stakeholder')}</h1>
          {!isDraft && s.groep && <p style={{ margin: '3px 0 0', color: '#8a90a0', fontSize: 14.5 }}>{s.groep}</p>}
        </div>
        <StakeholderForm s={s} locked={locked && !isDraft} onChange={(k, v) => (isDraft ? changeDraft(k, v) : update(s.id, k, v))} />
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 20 }}>
          {isDraft ? (
            <>
              <button onClick={back} style={{ padding: '10px 18px', borderRadius: 8, fontSize: 14, fontWeight: 600, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer' }}>Annuleren</button>
              <button onClick={saveDraft} style={{ padding: '10px 22px', borderRadius: 8, fontSize: 14, fontWeight: 700, border: 'none', background: BRAND.groen, color: '#fff', cursor: 'pointer' }}>Stakeholder toevoegen</button>
            </>
          ) : (
            <>
              {!locked
                ? <button onClick={() => remove(s.id)} style={{ padding: '10px 16px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, border: '1px solid #f0c9c9', background: '#fff', color: '#b91c1c', cursor: 'pointer' }}>Stakeholder verwijderen</button>
                : <span />}
              <button onClick={back} style={{ padding: '10px 22px', borderRadius: 8, fontSize: 14, fontWeight: 700, border: 'none', background: BRAND.groen, color: '#fff', cursor: 'pointer' }}>Klaar</button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ---- Overzicht ----
  return (
    <div style={{ padding: isMobile ? 16 : 32, maxWidth: 1120, margin: '0 auto' }}>
      <button onClick={() => navigate(`/project/${project.id}/omgevingsanalyse`)} style={backBtn}>
        <BackIcon />
        Terug naar omgevingsanalyse
      </button>

      <FaseTag nr={2} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <span style={{ width: 40, height: 40, borderRadius: 11, background: faseKleur(2).soft, color: BRAND.groen, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
        </span>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1a1a2e' }}>Stakeholders &amp; stakeholderanalyse</h1>
      </div>
      <p style={{ margin: '0 0 20px', color: '#6b7280', fontSize: 14.5, lineHeight: 1.6 }}>
        Wie worden geraakt door het project of hebben er invloed op? Bepaal per stakeholder invloed, belang en houding;
        de stakeholderbalans laat zien welke aanpak past. De stakeholders zijn input voor de persona's in Fase 3.
      </p>

      {locked && (
        <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 10, background: faseKleur(2).soft, border: `1px solid ${faseKleur(2).border}`, fontSize: 13.5, color: BRAND.groen }}>
          De omgevingsanalyse is gevalideerd. Heropen de fase in de{' '}
          <button onClick={() => navigate(`/project/${project.id}/omgevingsanalyse`)} style={{ padding: 0, border: 'none', background: 'none', color: BRAND.groen, fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit' }}>omgevingsanalyse</button>
          {' '}om stakeholders te wijzigen.
        </div>
      )}

      <StakeholdersOverview
        stakeholders={stakeholders}
        locked={locked}
        area={area}
        hasFase1={!!fase1Stakeholders.trim()}
        onOpen={open}
        onCreate={create}
        onSeed={seedArea}
        onSeedFase1={seedFase1}
        showHeading={false}
      />
    </div>
  );
};
